import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import type { OHLCVPoint } from '../../api/types';
import type { ConvictionTimePoint } from '../../api/useConvictionTimeseries';
import type { AlertItem } from '../../api/types';
import { alertSeverityColor } from '../../utils/colors';
import { plateLabel } from './tradePlate';
import {
    outcomeLabel,
    OUTCOME_MAXLEN_TOOLTIP,
} from '../../utils/outcomeLabels';
import {
    fmtUsd,
    fmtUsdSigned,
    fmtShares,
    fmtProb,
    fmtPrice,
    fmtVol,
    shortAddr,
    truncate,
} from './chartFormat';
import type {
    TradeOverlayData,
    PreparedSignal,
    PositionSnap,
} from './chartTypes';
import { SIGNAL_TYPE_LABELS } from './chartTypes';

/** Escape a third-party string for interpolation into tooltip HTML. */
export function esc(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** Position and show a tooltip div near the cursor, flipping if near viewport edge */
export function placeTooltip(
    tipEl: HTMLDivElement | null,
    event: MouseEvent,
    html: string
): void {
    if (!tipEl) return;
    const tip = d3.select(tipEl);
    tip.style('display', 'block').html(html);
    const OFFSET = 12;
    const tw = tipEl.offsetWidth;
    const th = tipEl.offsetHeight;
    const x =
        event.clientX + OFFSET + tw > window.innerWidth
            ? event.clientX - OFFSET - tw
            : event.clientX + OFFSET;
    const y =
        event.clientY + OFFSET + th > window.innerHeight
            ? event.clientY - OFFSET - th
            : event.clientY + OFFSET;
    tip.style('left', `${x}px`).style('top', `${y}px`);
}

// ─── Tooltip HTML builders ────────────────────────────────────────────────────

export interface TooltipColors {
    textColor: string;
    subColor: string;
    metaColor: string;
    sepStyle: string;
    isDark: boolean;
}

export function makeTooltipColors(
    textColor: string,
    isDark: boolean
): TooltipColors {
    return {
        textColor,
        subColor: isDark ? '#ccc' : '#555',
        metaColor: isDark ? '#888' : '#999',
        sepStyle: `border-top:1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};padding-top:3px;margin-top:3px`,
        isDark,
    };
}

export interface VolTooltipRow {
    label: string;
    color: string;
    vol: number;
    /** Running total for this row's stack identity up to the hovered bucket. */
    cum: number;
}

/**
 * Rows are supplied already labelled and coloured: the panel stacks by market in
 * a multi-market event and by outcome side for a lone market, and this builder
 * should not have to know which it was handed.
 */
export function buildVolTooltipHtml(
    rows: VolTooltipRow[],
    bucketDate: Date,
    tc: TooltipColors
): string {
    let html = `<div style="font-size:10px;color:${tc.textColor};margin-bottom:4px">${d3.timeFormat('%b %d, %H:%M')(bucketDate)}</div>`;
    const sorted = [...rows].sort((a, b) => b.cum - a.cum);
    for (const r of sorted) {
        html += `<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px">`;
        html += `<span style="width:6px;height:6px;border-radius:50%;background:${r.color};flex-shrink:0"></span>`;
        html += `<span style="font-size:10px;color:${tc.subColor};flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(truncate(r.label, 30))}</span>`;
        html += `<span style="font-family:monospace;font-size:10px;font-weight:700;white-space:nowrap">${fmtVol(r.vol)} <span style="font-weight:400;color:${tc.metaColor}">(${fmtVol(r.cum)})</span></span>`;
        html += `</div>`;
    }

    // The bar's height is the sum, so the tooltip states it rather than leaving
    // it to be added up by eye. Only worth a row when there is more than one.
    if (sorted.length > 1) {
        const vol = sorted.reduce((sum, r) => sum + r.vol, 0);
        const cum = sorted.reduce((sum, r) => sum + r.cum, 0);
        html += `<div style="display:flex;align-items:center;gap:4px;margin-top:3px;padding-top:3px;border-top:${tc.sepStyle}">`;
        html += `<span style="width:6px;flex-shrink:0"></span>`;
        html += `<span style="font-size:10px;color:${tc.textColor};flex:1;font-weight:600">Total</span>`;
        html += `<span style="font-family:monospace;font-size:10px;font-weight:700;white-space:nowrap">${fmtVol(vol)} <span style="font-weight:400;color:${tc.metaColor}">(${fmtVol(cum)})</span></span>`;
        html += `</div>`;
    }
    return html;
}

export function buildProbTooltipHtml(
    snapDate: Date,
    seriesValues: { conditionId: string; close: number }[],
    assetValues: { ticker: string; close: number; color: string }[],
    colorMap: Map<string, string>,
    questionMap: Map<string, string>,
    outcomesMap: Map<string, string[] | null> | undefined,
    tc: TooltipColors,
    showNoLine?: string | null,
    noColor?: string
): string {
    let html = `<div style="font-size:10px;color:${tc.textColor};margin-bottom:4px">${d3.timeFormat('%b %d, %Y %H:%M')(snapDate)}</div>`;
    for (const { conditionId, close } of seriesValues) {
        const color = colorMap.get(conditionId) ?? '#999';
        const label = esc(truncate(questionMap.get(conditionId) ?? '', 35));
        const isNoLine = showNoLine === conditionId;
        const outcomes = outcomesMap?.get(conditionId) ?? null;
        const yesName = esc(
            outcomeLabel(outcomes, 0, OUTCOME_MAXLEN_TOOLTIP)?.text ?? 'YES'
        );
        const noName = esc(
            outcomeLabel(outcomes, 1, OUTCOME_MAXLEN_TOOLTIP)?.text ?? 'NO'
        );

        const yesRow = () => {
            html += `<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px">`;
            html += `<span style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0"></span>`;
            if (isNoLine)
                html += `<span style="font-size:9px;font-weight:700;color:${color};font-family:monospace;flex-shrink:0">${yesName}:</span>`;
            html += `<span style="font-size:10px;color:${tc.subColor};flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${label}</span>`;
            html += `<span style="font-family:monospace;font-size:12px;font-weight:700;color:${color}">${(close * 100).toFixed(0)}%</span>`;
            html += `</div>`;
        };
        const noRow = () => {
            if (!isNoLine || !noColor) return;
            const noClose = 1 - close;
            html += `<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px">`;
            html += `<span style="width:8px;height:8px;border-radius:50%;background:${noColor};flex-shrink:0;opacity:0.7"></span>`;
            html += `<span style="font-size:9px;font-weight:700;color:${noColor};font-family:monospace;flex-shrink:0">${noName}:</span>`;
            html += `<span style="font-size:10px;color:${tc.subColor};flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${label}</span>`;
            html += `<span style="font-family:monospace;font-size:12px;font-weight:700;color:${noColor}">${(noClose * 100).toFixed(0)}%</span>`;
            html += `</div>`;
        };

        // Always show the higher-probability outcome first
        if (isNoLine && 1 - close > close) {
            noRow();
            yesRow();
        } else {
            yesRow();
            noRow();
        }
    }
    if (assetValues.length > 0) {
        for (const asset of assetValues) {
            const sep = `border-top:1px solid ${tc.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`;
            html += `<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px;${sep};padding-top:3px;margin-top:2px">`;
            html += `<span style="width:12px;height:2px;background:${asset.color};flex-shrink:0;border-radius:1px"></span>`;
            html += `<span style="font-size:10px;color:${tc.subColor};font-weight:600">${esc(asset.ticker)}</span>`;
            html += `<span style="font-family:monospace;font-size:12px;font-weight:700;color:${asset.color};margin-left:auto">${fmtPrice(asset.close)}</span>`;
            html += `</div>`;
        }
    }
    return html;
}

/** OHLCV breakdown for a single candle (shown on hover in candle mode). */
export function buildCandleTooltipHtml(
    pt: OHLCVPoint,
    upColor: string,
    downColor: string,
    tc: TooltipColors
): string {
    const closeColor = pt.close >= pt.open ? upColor : downColor;
    const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
    const row = (label: string, val: string, color?: string) =>
        `<div style="display:flex;justify-content:space-between;gap:12px">` +
        `<span style="font-size:10px;color:${tc.subColor}">${label}</span>` +
        `<span style="font-family:monospace;font-size:10px;font-weight:700;color:${color ?? tc.textColor}">${val}</span>` +
        `</div>`;
    return (
        `<div style="font-size:10px;color:${tc.textColor};margin-bottom:3px">${d3.timeFormat('%b %d, %Y %H:%M')(pt.t)}</div>` +
        row('O', pct(pt.open)) +
        row('H', pct(pt.high)) +
        row('L', pct(pt.low)) +
        row('C', pct(pt.close), closeColor) +
        row('Vol', fmtVol(pt.vol))
    );
}

export function buildMarkerTooltipHtml(
    sig: PreparedSignal,
    questionMap: Map<string, string>,
    tc: TooltipColors
): string {
    const question = esc(
        truncate(questionMap.get(sig.conditionId) ?? sig.conditionId, 52)
    );
    const openedStr = d3.timeFormat('%b %d, %H:%M')(sig.openedAt);
    const closedStr = sig.closedAt
        ? d3.timeFormat('%b %d, %H:%M')(sig.closedAt)
        : null;

    const fmtUsd = (v: number) => {
        const abs = Math.abs(v);
        const sign = v < 0 ? '−' : '+';
        if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
        if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
        return `${sign}$${abs.toFixed(0)}`;
    };
    const fmtProb = (v: number) => `${(v * 100).toFixed(1)}¢`;
    const fmtScore = (v: number) => {
        const pct = Math.round(v * 100);
        const bar =
            '█'.repeat(Math.round(pct / 10)) +
            '░'.repeat(10 - Math.round(pct / 10));
        return `${bar} ${pct}`;
    };

    const dirColor =
        sig.direction === 'bullish'
            ? '#10b981'
            : sig.direction === 'bearish'
              ? '#ef4444'
              : tc.metaColor;
    const dirLabel = sig.direction
        ? esc(sig.direction.toUpperCase())
        : 'NEUTRAL';
    const statusOpen = sig.status === 'open';
    const statusColor = statusOpen ? '#10b981' : tc.metaColor;
    const typeLabel = esc(
        sig.signalType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    );
    const groupColor =
        sig.traderGroup === 'insider'
            ? '#f59e0b'
            : sig.traderGroup === 'winner'
              ? '#10b981'
              : sig.traderGroup === 'whale'
                ? '#6366f1'
                : tc.metaColor;

    const sep = `border-top:1px solid ${tc.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};margin-top:6px;padding-top:6px`;

    let h = '';

    // ── Header row: type badge + status pill ──────────────────────────────
    h += `<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:4px">`;
    h += `<span style="font-family:monospace;font-size:11px;font-weight:700;color:${sig.color}">${typeLabel}</span>`;
    h +=
        `<span style="font-family:monospace;font-size:8px;font-weight:600;color:${statusColor};` +
        `border:1px solid ${statusColor}44;border-radius:3px;padding:1px 5px;letter-spacing:0.05em">` +
        `${statusOpen ? '● OPEN' : '○ CLOSED'}</span>`;
    h += `</div>`;

    // ── Market question ───────────────────────────────────────────────────
    h += `<div style="font-size:10px;color:${tc.subColor};margin-bottom:6px;line-height:1.35">${question}</div>`;

    // ── Trader row: group badge + address ─────────────────────────────────
    if (sig.address || sig.traderGroup !== 'none') {
        h += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">`;
        if (sig.traderGroup !== 'none') {
            h +=
                `<span style="font-family:monospace;font-size:8px;font-weight:700;` +
                `color:${groupColor};background:${groupColor}22;` +
                `border:1px solid ${groupColor}44;border-radius:3px;padding:1px 5px">` +
                `${esc(sig.traderGroup.toUpperCase())}</span>`;
        }
        if (sig.address) {
            const alias = esc(sig.addressAlias ?? shortAddr(sig.address));
            h += `<span style="font-family:monospace;font-size:9px;color:${tc.metaColor}">${alias}</span>`;
        }
        h += `</div>`;
    }

    // ── Direction + score bar ─────────────────────────────────────────────
    h += `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">`;
    h += `<span style="font-family:monospace;font-size:10px;font-weight:700;color:${dirColor}">${dirLabel}</span>`;
    h +=
        `<span style="font-family:monospace;font-size:9px;color:${tc.metaColor}" title="conviction score">` +
        `${fmtScore(sig.score)}</span>`;
    h += `</div>`;

    // ── Fact block ────────────────────────────────────────────────────────
    if (sig.factDeltaUsd !== null || sig.factSide !== null) {
        h += `<div style="${sep}">`;
        h += `<div style="font-size:8px;font-weight:700;letter-spacing:0.06em;color:${tc.metaColor};margin-bottom:3px">FACT</div>`;
        h += `<div style="display:flex;gap:12px;flex-wrap:wrap">`;
        if (sig.factSide) {
            const sideColor = sig.factSide === 'YES' ? '#10b981' : '#ef4444';
            h += `<div style="font-size:10px;color:${tc.subColor}">side <span style="font-weight:700;color:${sideColor}">${esc(sig.factSide)}</span></div>`;
        }
        if (sig.factDeltaUsd !== null) {
            const capColor = sig.factDeltaUsd >= 0 ? '#10b981' : '#ef4444';
            h += `<div style="font-size:10px;color:${tc.subColor}">Δ capital <span style="font-family:monospace;font-weight:700;color:${capColor}">${fmtUsd(sig.factDeltaUsd)}</span></div>`;
        }
        h += `</div>`;
        h += `</div>`;
    }

    // ── Session prices ────────────────────────────────────────────────────
    const hasPrices =
        sig.sessionOpeningPrice !== null ||
        sig.sessionPeakPrice !== null ||
        sig.sessionClosePrice !== null;
    if (hasPrices) {
        h += `<div style="${sep}">`;
        h += `<div style="font-size:8px;font-weight:700;letter-spacing:0.06em;color:${tc.metaColor};margin-bottom:3px">SESSION</div>`;
        h += `<div style="display:flex;gap:12px;font-size:10px;font-family:monospace">`;
        if (sig.sessionOpeningPrice !== null)
            h += `<div style="color:${tc.subColor}">entry <span style="font-weight:600;color:${tc.textColor}">${fmtProb(sig.sessionOpeningPrice)}</span></div>`;
        if (sig.sessionPeakPrice !== null)
            h += `<div style="color:${tc.subColor}">peak <span style="font-weight:600;color:#f59e0b">${fmtProb(sig.sessionPeakPrice)}</span></div>`;
        if (sig.sessionClosePrice !== null)
            h += `<div style="color:${tc.subColor}">exit <span style="font-weight:600;color:${tc.textColor}">${fmtProb(sig.sessionClosePrice)}</span></div>`;
        h += `</div>`;
        h += `</div>`;
    }

    // ── Timestamp footer ──────────────────────────────────────────────────
    h += `<div style="${sep};font-family:monospace;font-size:9px;color:${tc.metaColor}">`;
    h += `${openedStr}`;
    if (closedStr) h += ` <span style="opacity:0.5">→</span> ${closedStr}`;
    h += `</div>`;

    return h;
}

export function buildSpanTooltipHtml(
    address: string,
    sigs: PreparedSignal[],
    successColor: string,
    tc: TooltipColors
): string {
    const alias = esc(sigs[0].addressAlias ?? shortAddr(address));
    const group = sigs[0].traderGroup;
    const first = sigs.reduce((a, b) => (a.openedAt < b.openedAt ? a : b));
    const last = sigs.reduce((a, b) => {
        const aEnd = a.closedAt ?? a.lastSeenAt ?? a.openedAt;
        const bEnd = b.closedAt ?? b.lastSeenAt ?? b.openedAt;
        return aEnd > bEnd ? a : b;
    });
    const isStillOpen = last.status === 'open' && !last.closedAt;
    const totalCapital = sigs.reduce(
        (sum, s) => sum + (s.factDeltaUsd ?? 0),
        0
    );
    const typesSeen = [
        ...new Set(
            sigs.map(s => SIGNAL_TYPE_LABELS[s.signalType] ?? s.signalType)
        ),
    ];
    const timeFmt = d3.timeFormat('%b %d, %H:%M');

    let h = `<div style="font-size:10px;color:${tc.textColor};font-weight:600;margin-bottom:2px">${alias}</div>`;
    h += `<div style="font-family:monospace;font-size:9px;color:${tc.metaColor};margin-bottom:3px">${group !== 'none' ? esc(group) : 'unclassified'}</div>`;
    h += `<div style="${tc.sepStyle}">`;
    h += `<div style="font-size:9px;color:${tc.subColor}">actions: <b>${esc(typesSeen.join(', '))}</b></div>`;
    if (totalCapital !== 0)
        h += `<div style="font-size:9px;color:${tc.subColor}">total Δ: <b>${fmtUsd(totalCapital)}</b></div>`;
    if (first.sessionOpeningPrice !== null)
        h += `<div style="font-size:9px;color:${tc.subColor}">entry price: <b>${fmtProb(first.sessionOpeningPrice)}</b></div>`;
    h += `</div>`;
    h += `<div style="${tc.sepStyle};font-family:monospace;font-size:9px;color:${tc.metaColor}">`;
    h += timeFmt(first.openedAt);
    if (isStillOpen) {
        h += ` → <span style="color:${successColor}">still open</span>`;
    } else if (last.closedAt) {
        h += ` → ${timeFmt(last.closedAt)}`;
    }
    h += ` (${sigs.length} signal${sigs.length !== 1 ? 's' : ''})`;
    h += `</div>`;
    return h;
}

export function buildPositionTooltipHtml(
    positionsByAccount: Map<string, PositionSnap[]>,
    hoverDate: Date,
    currentProb: number,
    accountColorMap: Map<string, string>,
    tc: TooltipColors
): string {
    const pnlColor = (n: number) =>
        n >= 0
            ? tc.isDark
                ? '#4ade80'
                : '#16a34a'
            : tc.isDark
              ? '#f87171'
              : '#dc2626';
    const bisectSnap = d3.bisector<PositionSnap, Date>(d => d.t).left;

    let html = `<div style="font-size:10px;color:${tc.textColor};margin-bottom:5px">${d3.timeFormat('%b %d, %H:%M')(hoverDate)} · <span style="font-size:9px;opacity:0.7">p=${(currentProb * 100).toFixed(1)}¢</span></div>`;

    for (const [account, snaps] of positionsByAccount) {
        const acctColor = accountColorMap.get(account) ?? '#888';
        const displayAddr = esc(
            account.length > 14 ? shortAddr(account) : account
        );
        const si = bisectSnap(snaps, hoverDate, 1);
        if (si === 0) continue;
        const snap = snaps[si - 1];

        const unrealizedPnl =
            snap.yesShares * currentProb +
            snap.noShares * (1 - currentProb) -
            snap.yesCostBasis -
            snap.noCostBasis;
        const totalPnl = snap.realizedPnl + unrealizedPnl;

        const breakdown =
            snap.yesShares > 0 || snap.noShares > 0
                ? ` <span style="font-size:9px;opacity:0.6">(Y:${fmtShares(snap.yesShares)} N:${fmtShares(snap.noShares)})</span>`
                : '';

        html += `<div style="margin-bottom:4px;padding-bottom:4px;border-bottom:1px solid ${tc.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}">`;
        html += `<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px">`;
        html += `<span style="width:6px;height:6px;border-radius:50%;background:${acctColor};display:inline-block;flex-shrink:0"></span>`;
        html += `<span style="font-family:monospace;font-size:9px;color:${acctColor}">${displayAddr}</span>`;
        html += `</div>`;
        html += `<div style="font-family:monospace;font-size:10px;font-weight:600;color:${tc.textColor}">${fmtShares(snap.pos)}${breakdown}</div>`;
        html += `<div style="display:flex;gap:8px;font-family:monospace;font-size:9px;margin-top:2px">`;
        html += `<span style="color:${pnlColor(totalPnl)}">total ${fmtUsdSigned(totalPnl)}</span>`;
        html += `<span style="opacity:0.65">real. ${fmtUsdSigned(snap.realizedPnl)}</span>`;
        html += `<span style="opacity:0.65">unreal. ${fmtUsdSigned(unrealizedPnl)}</span>`;
        html += `</div>`;
        html += `</div>`;
    }
    return html;
}

export function buildTradeTooltipHtml(
    trade: TradeOverlayData['trades'][number],
    dotColor: string,
    successColor: string,
    errorColor: string,
    tc: TooltipColors,
    // When a NO trade is plotted on the YES scale, the YES-equivalent price
    // (1 - price) it maps to. Shown as an extra "YES price" row.
    yesEquivPrice?: number | null,
    outcomes?: string[] | null
): string {
    const sideColor = trade.side === 'buy' ? successColor : errorColor;
    const plate = plateLabel(
        trade.side,
        trade.outcomeIndex,
        outcomes,
        OUTCOME_MAXLEN_TOOLTIP
    );
    const displayAddr = esc(
        trade.account.length > 14 ? shortAddr(trade.account) : trade.account
    );
    const yesName =
        outcomeLabel(outcomes, 0, OUTCOME_MAXLEN_TOOLTIP)?.text ?? 'YES';
    const yesRow =
        yesEquivPrice != null
            ? `<div style="display:flex;justify-content:space-between;gap:12px;margin-bottom:1px">` +
              `<span style="font-size:10px;color:${tc.subColor}">${esc(yesName)} price</span>` +
              `<span style="font-family:monospace;font-size:10px;font-weight:700;color:${successColor}">${(yesEquivPrice * 100).toFixed(1)}¢</span>` +
              `</div>`
            : '';
    return (
        `<div style="font-size:10px;color:${tc.textColor};margin-bottom:3px">${d3.timeFormat('%b %d, %Y %H:%M:%S')(trade.timestamp)}</div>` +
        `<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px">` +
        // No title attribute — the tooltip box is pointer-events:none, so it could never be hovered.
        `<span style="font-size:9px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:${sideColor};background:${alpha(sideColor, 0.15)};padding:1px 5px;border-radius:3px;white-space:nowrap">${esc(plate.text)}</span>` +
        `<span style="font-size:10px;color:${tc.subColor}">@ ${(trade.price * 100).toFixed(1)}¢</span>` +
        `</div>` +
        yesRow +
        `<div style="font-family:monospace;font-size:10px;color:${tc.isDark ? '#aaa' : '#666'};margin-bottom:1px">` +
        `Qty: ${trade.amount.toFixed(2)} · $${trade.quoteAmount.toFixed(2)}` +
        `</div>` +
        `<div style="font-family:monospace;font-size:9px;color:${dotColor}">${displayAddr}</div>`
    );
}

export function buildConvictionTooltipHtml(
    pt: ConvictionTimePoint,
    groupName: string,
    groupColor: string,
    isRaw: boolean,
    tc: TooltipColors
): string {
    const fmtUsd = (v: number) => {
        const abs = Math.abs(v);
        const sign = v < 0 ? '−' : v > 0 ? '+' : '';
        if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
        if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
        return `${sign}$${abs.toFixed(0)}`;
    };
    const fmtUsdAbs = (v: number) => {
        const abs = Math.abs(v);
        if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`;
        if (abs >= 1_000) return `$${(abs / 1_000).toFixed(1)}K`;
        return `$${abs.toFixed(0)}`;
    };

    const netCapColor = pt.net_capital >= 0 ? '#10b981' : '#ef4444';
    const netTrdColor = pt.net_traders >= 0 ? '#10b981' : '#ef4444';
    const modeTag = isRaw
        ? `<span style="font-size:7px;letter-spacing:0.04em;opacity:0.5;margin-left:4px">RAW</span>`
        : '';

    let h = '';

    // ── Header: timestamp + group badge ───────────────────────────────────────
    h += `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px">`;
    h += `<span style="font-size:10px;color:${tc.textColor}">${d3.timeFormat('%b %d, %H:%M')(pt.ts)}</span>`;
    h +=
        `<span style="font-family:monospace;font-size:8px;font-weight:700;color:${groupColor};` +
        `background:${groupColor}22;border:1px solid ${groupColor}44;border-radius:3px;padding:1px 5px">` +
        `${esc(groupName.toUpperCase())}${modeTag}</span>`;
    h += `</div>`;

    // ── Implied probability row ────────────────────────────────────────────────
    if (pt.raw_implied != null) {
        const impliedColor =
            pt.raw_implied > 0.5
                ? '#10b981'
                : pt.raw_implied < 0.5
                  ? '#ef4444'
                  : tc.subColor;
        h +=
            `<div style="display:flex;align-items:baseline;justify-content:space-between;` +
            `margin-bottom:5px;padding-bottom:5px;` +
            `border-bottom:1px solid ${tc.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}">`;
        h += `<span style="font-size:9px;color:${tc.metaColor};letter-spacing:0.04em">IMPLIED PROB</span>`;
        h += `<span style="font-family:monospace;font-size:13px;font-weight:700;color:${impliedColor}">${(pt.raw_implied * 100).toFixed(1)}¢</span>`;
        h += `</div>`;
    }

    // ── Net capital row ────────────────────────────────────────────────────────
    h += `<div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:2px">`;
    h += `<span style="font-size:9px;color:${tc.metaColor};letter-spacing:0.04em">NET CAPITAL</span>`;
    h += `<span style="font-family:monospace;font-size:13px;font-weight:700;color:${netCapColor}">${fmtUsd(pt.net_capital)}</span>`;
    h += `</div>`;

    // YES / NO breakdown for capital
    h +=
        `<div style="display:flex;gap:6px;margin-bottom:5px;padding-bottom:5px;` +
        `border-bottom:1px solid ${tc.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}">`;
    h += `<span style="font-size:9px;font-family:monospace;color:#10b981">`;
    h += `YES <span style="font-weight:600">${fmtUsdAbs(pt.capital_usd_yes)}</span></span>`;
    h += `<span style="font-size:9px;color:${tc.metaColor}">·</span>`;
    h += `<span style="font-size:9px;font-family:monospace;color:#ef4444">`;
    h += `NO <span style="font-weight:600">${fmtUsdAbs(pt.capital_usd_no)}</span></span>`;
    h += `</div>`;

    // ── Net trader count row ───────────────────────────────────────────────────
    h += `<div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:2px">`;
    h += `<span style="font-size:9px;color:${tc.metaColor};letter-spacing:0.04em">NET TRADERS</span>`;
    const trdStr =
        pt.net_traders > 0 ? `+${pt.net_traders}` : String(pt.net_traders);
    h += `<span style="font-family:monospace;font-size:13px;font-weight:700;color:${netTrdColor}">${trdStr}</span>`;
    h += `</div>`;

    // YES / NO breakdown for traders
    h += `<div style="display:flex;gap:6px">`;
    h += `<span style="font-size:9px;font-family:monospace;color:#10b981">`;
    h += `YES <span style="font-weight:600">${pt.n_traders_yes}</span></span>`;
    h += `<span style="font-size:9px;color:${tc.metaColor}">·</span>`;
    h += `<span style="font-size:9px;font-family:monospace;color:#ef4444">`;
    h += `NO <span style="font-weight:600">${pt.n_traders_no}</span></span>`;
    h += `</div>`;

    // ── Open shares net row ────────────────────────────────────────────────────
    const openSharesNet = pt.open_shares_yes - pt.open_shares_no;
    const openSharesColor = openSharesNet >= 0 ? '#10b981' : '#ef4444';
    const openSharesStr =
        openSharesNet > 0
            ? `+${fmtShares(openSharesNet)}`
            : fmtShares(openSharesNet);
    h +=
        `<div style="display:flex;align-items:baseline;justify-content:space-between;` +
        `margin-top:5px;padding-top:5px;` +
        `border-top:1px solid ${tc.isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}">`;
    h += `<span style="font-size:9px;color:${tc.metaColor};letter-spacing:0.04em">OPEN SHARES NET</span>`;
    h += `<span style="font-family:monospace;font-size:11px;font-weight:700;color:${openSharesColor}">${openSharesStr}</span>`;
    h += `</div>`;

    return h;
}

export function buildAlertTooltipHtml(
    alert: AlertItem,
    tc: TooltipColors
): string {
    const severityLabel =
        alert.severity === 'critical'
            ? 'CRITICAL'
            : alert.severity === 'warning'
              ? 'WARNING'
              : 'INFO';

    const severityColor = alertSeverityColor(alert.severity, '#888');

    const tsStr = d3.timeFormat('%b %d, %Y %H:%M')(new Date(alert.ts));
    const typeLabel = esc(alert.alert_type.replace(/_/g, ' '));

    let h = '';
    h += `<div style="display:flex;align-items:center;gap:5px;margin-bottom:4px">`;
    h += `<span style="font-size:9px;font-weight:700;letter-spacing:0.06em;color:${severityColor};font-family:monospace">${severityLabel}</span>`;
    h += `<span style="font-size:9px;color:${tc.metaColor};font-family:monospace">${typeLabel}</span>`;
    h += `</div>`;
    h += `<div style="font-size:11px;font-weight:600;color:${tc.textColor};margin-bottom:4px;line-height:1.35;max-width:240px">${esc(alert.title)}</div>`;
    if (alert.detail) {
        h += `<div style="font-size:10px;color:${tc.subColor};margin-bottom:4px;line-height:1.4;max-width:240px">${esc(alert.detail)}</div>`;
    }
    if (alert.current_yes_prob != null) {
        const pct = Math.round(alert.current_yes_prob * 100);
        h += `<div style="${tc.sepStyle};display:flex;align-items:center;justify-content:space-between;gap:8px">`;
        h += `<span style="font-family:monospace;font-size:9px;color:${tc.metaColor}">PROBABILITY AT TRIGGER</span>`;
        h += `<span style="font-family:monospace;font-size:11px;font-weight:700;color:${tc.textColor}">${pct}%</span>`;
        h += `</div>`;
    }
    h += `<div style="${tc.sepStyle};font-family:monospace;font-size:9px;color:${tc.metaColor}">${tsStr}</div>`;
    return h;
}

export function buildSignalTypeColors(palette: {
    success: { main: string };
    error: { main: string };
    info: { main: string; light: string };
    warning: { main: string };
}): Record<string, string> {
    return {
        position_opened: palette.success.main,
        position_increased: palette.success.main,
        position_decreased: palette.error.main,
        position_change: palette.info.main,
        probability_spike: palette.warning.main,
        probability_change: palette.info.light,
        volume_anomaly: palette.info.light,
    };
}

// ─── Trade cluster ────────────────────────────────────────────────────────────

/**
 * A cluster stands for trades too tightly packed to draw apart, so it reports
 * what the reader can no longer count: how many, how much, and over what span.
 */
export function buildClusterTooltipHtml(
    cluster: {
        count: number;
        totalQuote: number;
        side: 'buy' | 'sell';
        minMs: number | null;
        maxMs: number | null;
    },
    avgPrice: number,
    color: string,
    tc: TooltipColors
): string {
    const verb = cluster.side === 'buy' ? 'buys' : 'sells';
    const span =
        cluster.minMs !== null && cluster.maxMs !== null
            ? cluster.minMs === cluster.maxMs
                ? d3.timeFormat('%b %d, %H:%M')(new Date(cluster.minMs))
                : `${d3.timeFormat('%b %d, %H:%M')(new Date(cluster.minMs))} – ${d3.timeFormat('%H:%M')(new Date(cluster.maxMs))}`
            : '';
    let html = '';
    if (span)
        html += `<div style="font-size:10px;color:${tc.textColor};margin-bottom:4px">${span}</div>`;
    html += `<div style="display:flex;align-items:center;gap:6px;white-space:nowrap">`;
    html += `<span style="width:8px;height:8px;border-radius:2px;background:${color};display:inline-block"></span>`;
    html += `<b style="font-family:monospace;font-size:11px;color:${color}">${cluster.count} ${verb}</b>`;
    html += `<span style="font-family:monospace;font-size:11px;font-weight:700">${fmtVol(cluster.totalQuote)}</span>`;
    html += `<span style="font-size:10px;color:${tc.subColor}">avg ${(avgPrice * 100).toFixed(1)}%</span>`;
    html += `</div>`;
    return html;
}
